import { MutableRefObject, useEffect, useRef, useState } from 'react'

import styles from './useTransitions.module.scss'

type Status = 'hidden' | 'mounting' | 'entering' | 'entered' | 'exiting'

const MOUNTING: Status = 'mounting'
const HIDDEN: Status = 'hidden'
const ENTERING: Status = 'entering'
const ENTERED: Status = 'entered'
const EXITING: Status = 'exiting'

type TransitionOptions = {
  skipTransition?: boolean
  transitionInOnMount?: boolean
  displayNoneOnHidden?: boolean

  duration?: number
  inDuration?: number
  outDuration?: number

  enteringClassName?: string
  exitingClassName?: string
  hiddenClassName?: string
}

export const useTransition = (
  visable: boolean,
  options?: TransitionOptions,
): [MutableRefObject<HTMLDivElement | null>, Status] => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [status, setStatus] = useState<Status>(visable ? (options?.transitionInOnMount ? ENTERING : ENTERED) : HIDDEN)

  const handleSetEntering = () => {
    ref.current?.classList.remove(styles.hidden)
    ref.current?.classList.remove(styles.exiting)
    ref.current?.classList.add(styles.entering)

    if (options?.hiddenClassName) ref.current?.classList.remove(options.hiddenClassName)
    if (options?.exitingClassName) ref.current?.classList.remove(options.exitingClassName)
    if (options?.enteringClassName) ref.current?.classList.add(options.enteringClassName)

    if (ref.current && (options?.duration || options?.inDuration)) {
      ref.current.style.animationDuration = `${options.inDuration ?? options.duration}ms`
    }

    setStatus(ENTERING)
  }

  const handleSetEntered = () => {
    ref.current?.classList.remove(styles.entering)
    ref.current?.classList.remove(styles.exiting)
    ref.current?.classList.remove(styles.hidden)

    if (options?.enteringClassName) ref.current?.classList.remove(options.enteringClassName)
    if (options?.exitingClassName) ref.current?.classList.remove(options.exitingClassName)
    if (options?.hiddenClassName) ref.current?.classList.remove(options.hiddenClassName)

    setStatus(ENTERED)
  }

  const handleSetExiting = () => {
    ref.current?.classList.remove(styles.entering)
    ref.current?.classList.remove(styles.hidden)
    ref.current?.classList.add(styles.exiting)

    if (options?.enteringClassName) ref.current?.classList.remove(options.enteringClassName)
    if (options?.hiddenClassName) ref.current?.classList.remove(options.hiddenClassName)
    if (options?.exitingClassName) ref.current?.classList.add(options.exitingClassName)

    if (ref.current && (options?.duration || options?.outDuration)) {
      ref.current.style.animationDuration = `${options.outDuration ?? options.duration}ms`
    }

    setStatus(EXITING)
  }

  const handleSetHidden = (mounting?: boolean) => {
    ref.current?.classList.remove(styles.entering)
    ref.current?.classList.remove(styles.exiting)
    ref.current?.classList.add(styles.hidden)

    if (options?.enteringClassName) ref.current?.classList.remove(options.enteringClassName)
    if (options?.exitingClassName) ref.current?.classList.remove(options.exitingClassName)
    if (options?.hiddenClassName) ref.current?.classList.add(options.hiddenClassName)

    if (ref.current && options?.displayNoneOnHidden) {
      ref.current.style.display = 'none'
    }

    if (mounting) return setStatus(MOUNTING)

    setStatus(HIDDEN)
  }

  const handleTransitionEnd = (e: AnimationEvent) => {
    if (e.target !== ref.current) return

    if (visable) handleSetEntered()
    if (!visable) handleSetHidden()

    if ((options?.duration || options?.inDuration || options?.outDuration) && ref.current)
      ref.current.style.animationDuration = ''
  }

  useEffect(() => {
    if (visable) {
      if (status === MOUNTING) return handleSetEntering()

      // The hook was mounted hidden, but the element we want to transition in wasn't mounted (presumably hidden via status === 'hidden')
      // Set the status to 'mounting' with all the styles of a hidden element to prevent it from flashing, then let this useEffect run again
      if (status === HIDDEN && !ref.current) return handleSetHidden(true)

      if (status === ENTERED) return // Already open

      if (options?.displayNoneOnHidden && ref.current) ref.current.style.display = ''

      if (!options?.skipTransition) handleSetEntering()
      if (options?.skipTransition) handleSetEntered()
    }

    if (!visable) {
      if (status === HIDDEN || status === EXITING) return // Do nothing, we're already hidden or transitioning out

      if (!options?.skipTransition) handleSetExiting()
      if (options?.skipTransition) handleSetHidden()
    }
  }, [visable, status])

  useEffect(() => {
    ref?.current?.addEventListener('animationend', handleTransitionEnd)

    return () => {
      ref?.current?.removeEventListener('animationend', handleTransitionEnd)
    }
  }, [ref.current, visable, options])

  return [ref, status]
}
